import { ReactNode, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AppBar, Box, IconButton, Toolbar, Typography, useMediaQuery, useTheme } from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth'
import { KidBottomNav } from './kids/KidBottomNav'
import { grantKidParentAccess } from '../lib/kidRoutes'

interface KidLayoutProps {
  children: ReactNode
}

export function KidLayout({ children }: KidLayoutProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user } = useAuth()

  const isReaderSession = location.pathname.startsWith('/reader')
  const isGameSession = /^\/games\/[^/]+/.test(location.pathname)
  const hideChrome = isReaderSession || isGameSession

  const headerTitle = useMemo(() => {
    const firstName = (user?.displayName ?? '').split(' ')[0]
    return firstName ? t('kids.helloName', { name: firstName }) : t('kids.homeTitle')
  }, [user?.displayName, t])

  const openParentSettings = () => {
    grantKidParentAccess()
    navigate('/settings')
  }

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100dvh',
        minWidth: 0,
        background: (th) =>
          th.palette.mode === 'light'
            ? 'linear-gradient(180deg, rgba(20,184,166,0.08) 0%, rgba(249,115,22,0.05) 35%, transparent 70%)'
            : 'linear-gradient(180deg, rgba(45,212,191,0.10) 0%, transparent 45%)',
      }}
    >
      {!hideChrome && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            bgcolor: 'transparent',
            backgroundImage: 'none',
            boxShadow: 'none',
          }}
        >
          <Toolbar
            variant={isMobile ? 'dense' : 'regular'}
            sx={{
              pt: 'var(--safe-top, 0px)',
              minHeight: {
                xs: `calc(56px + var(--safe-top, 0px))`,
                sm: `calc(64px + var(--safe-top, 0px))`,
              },
            }}
          >
            <Typography
              variant={isMobile ? 'subtitle1' : 'h6'}
              noWrap
              sx={{
                flexGrow: 1,
                fontWeight: 900,
                letterSpacing: '-0.02em',
                color: 'text.primary',
              }}
            >
              {headerTitle}
            </Typography>
            <IconButton
              aria-label={t('kids.parentLock')}
              onClick={openParentSettings}
              sx={{
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: '20px',
                minWidth: 56,
                minHeight: 56,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <LockIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          ...(hideChrome
            ? {
                px: 0,
                py: 0,
                pb: 0,
                mt: 0,
                minHeight: 0,
                height: '100%',
              }
            : {
                px: { xs: 0, sm: 2, md: 3 },
                py: { xs: 1.5, sm: 2, md: 3 },
                pb: 3,
                mt: {
                  xs: 'calc(56px + var(--safe-top, 0px))',
                  sm: 'calc(64px + var(--safe-top, 0px))',
                  md: '64px',
                },
              }),
        }}
      >
        {children}
        {user && !hideChrome && (
          <Box
            aria-hidden
            sx={{
              height: 'calc(120px + var(--safe-bottom, 0px))',
              flexShrink: 0,
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>

      {user && !hideChrome && <KidBottomNav />}
    </Box>
  )
}

